/**
 * מנוע טוקנים + אלמנטי בלנק לצד הלקוח (דוחות חברים)
 */

export function parseTokenStyleExtra(extra = '') {
  const styles = {};
  if (!extra) return styles;
  for (const part of String(extra).split('|')) {
    const idx = part.indexOf(':');
    if (idx < 0) continue;
    const k = part.slice(0, idx);
    const v = part.slice(idx + 1);
    if (k === 'fs') styles.fontSize = v;
    else if (k === 'ff') styles.fontFamily = v;
    else if (k === 'fw' && v === 'bold') styles.fontWeight = 'bold';
    else if (k === 'fi' && v === 'italic') styles.fontStyle = 'italic';
    else if (k === 'td' && v === 'underline') styles.textDecoration = 'underline';
  }
  return styles;
}

export function encodeTokenStyleExtra(styles = {}) {
  const parts = [];
  if (styles.fontSize) parts.push(`fs:${styles.fontSize}`);
  if (styles.fontFamily) parts.push(`ff:${styles.fontFamily}`);
  if (styles.fontWeight === 'bold') parts.push('fw:bold');
  if (styles.fontStyle === 'italic') parts.push('fi:italic');
  if (styles.textDecoration?.includes('underline')) parts.push('td:underline');
  return parts.length ? `|${parts.join('|')}` : '';
}

export function encodeStyledToken(base, styles = {}) {
  return `{{${base}${encodeTokenStyleExtra(styles)}}}`;
}

export function stylesToInlineAttr(styles = {}) {
  const parts = [];
  if (styles.fontSize) parts.push(`font-size:${styles.fontSize}`);
  if (styles.fontFamily) parts.push(`font-family:${styles.fontFamily}`);
  if (styles.fontWeight === 'bold') parts.push('font-weight:bold');
  if (styles.fontStyle === 'italic') parts.push('font-style:italic');
  if (styles.textDecoration) parts.push(`text-decoration:${styles.textDecoration}`);
  return parts.join(';');
}

export function wrapValueWithStyles(value, styles = {}) {
  const attr = stylesToInlineAttr(styles);
  if (!attr) return String(value ?? '');
  return `<span style="${attr}">${String(value ?? '')}</span>`;
}

export function replaceStyledTokens(html, resolveValue) {
  return String(html).replace(/\{\{([^}|]+)(?:\|([^}]+))?\}\}/g, (match, base, styleExtra) => {
    if (String(base).startsWith('blank:')) return match;
    const value = resolveValue(base, match);
    return wrapValueWithStyles(value, parseTokenStyleExtra(styleExtra || ''));
  });
}

export const BLANK_BLOCKS = [
  { key: 'line', label: 'קו לכתיבה' },
  { key: 'lines_2', label: '2 קווים' },
  { key: 'lines_sticker', label: '3 קווים' },
  { key: 'signature', label: 'שורת חתימה' },
  { key: 'notes', label: 'משבצת הערות' },
  { key: 'spacer', label: 'רווח' },
];

export const DESIGN_ELEMENTS = BLANK_BLOCKS;

const BLANK_LABEL_BY_KEY = Object.fromEntries(BLANK_BLOCKS.map((b) => [b.key, b.label]));

export function blankToken(key, styles = {}) {
  return encodeStyledToken(`blank:${key}`, styles);
}

export function renderBlankHtml(key) {
  switch (key) {
    case 'line':
      return '<div class="report-blank-line"></div>';
    case 'lines_2':
      return '<div class="report-blank-line"></div><div class="report-blank-line"></div>';
    case 'lines_sticker':
      return '<div class="report-blank-sticker-lines"><div class="write-line full"></div><div class="write-line full"></div><div class="write-line full"></div></div>';
    case 'signature':
      return '<div class="report-signature"><span class="lbl">חתימה:</span><span class="line"></span></div>';
    case 'notes':
      return '<div class="report-notes-box"></div>';
    case 'spacer':
      return '<div class="report-spacer"></div>';
    default:
      return '';
  }
}

export function applyBlankTokens(html = '') {
  return String(html).replace(/\{\{blank:([^}|]+)(?:\|([^}]+))?\}\}/g, (match, key, styleExtra) => {
    const block = renderBlankHtml(key);
    const attr = stylesToInlineAttr(parseTokenStyleExtra(styleExtra || ''));
    if (!attr) return block;
    return `<div style="${attr}">${block}</div>`;
  });
}

export function blankFieldSpanHtml(key) {
  const label = BLANK_LABEL_BY_KEY[key] || key;
  return `<span class="box-blank-field" contenteditable="false" data-blank="${key}" style="background:#fff3e0;color:#e65100;padding:1px 8px;border-radius:4px;border:1px dashed #ffb74d;">[${label}]</span>`;
}

export function blanksToDisplayHtml(html = '') {
  return String(html).replace(/\{\{blank:([^}|]+)(?:\|([^}]+))?\}\}/g, (_, key) => blankFieldSpanHtml(key));
}

export function displayHtmlToBlankTokens(html = '') {
  let out = String(html);
  out = out.replace(
    /<span[^>]*data-blank="([^"]+)"[^>]*>[\s\S]*?<\/span>/gi,
    (_, key) => blankToken(key)
  );
  for (const { key, label } of BLANK_BLOCKS) {
    out = out.replace(new RegExp(`\\[${label}\\]`, 'g'), blankToken(key));
  }
  return out;
}

export function blanksToHebrewPreview(html = '') {
  return String(html).replace(/\{\{blank:([^}|]+)(?:\|([^}]+))?\}\}/g, (_, key) => (
    `[${BLANK_LABEL_BY_KEY[key] || key}]`
  ));
}

export const REPORT_BLANK_PRINT_CSS = `
  .report-blank-line { border-bottom: 1px solid #000; height: 18px; margin: 8px 0; }
  .report-blank-sticker-lines { margin-top: 8px; }
  .report-blank-sticker-lines .write-line.full { border-bottom: 1px solid #000; height: 18px; margin-bottom: 6px; }
  .report-signature { display: flex; align-items: flex-end; gap: 8px; margin-top: 24px; }
  .report-signature .lbl { font-weight: 700; }
  .report-signature .line { flex: 1; border-bottom: 1px solid #000; height: 18px; max-width: 200px; }
  .report-notes-box { border: 1px solid #999; min-height: 60px; margin: 10px 0; }
  .report-spacer { height: 20px; }
`;
